import * as THREE from "three";
import Orchestrator from "./Orchestrator.js";
import { device } from "./Utils/device.js";
import { CONFIG } from "./Stage/config.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const LOOK_SENSITIVITY = 0.003;
const PITCH_LIMIT = Math.PI / 2 - 0.01;

export default class Camera {
    constructor() {
        this.orc = new Orchestrator();
        this.sizes = this.orc.sizes;
        this.scene = this.orc.scene;
        this.canvas = this.orc.canvas;

        this.flying = false;
        this.flySpeed = 80;
        this._flyDir = new THREE.Vector3();
        this._euler = new THREE.Euler(0, 0, 0, "YXZ");
        this._yaw = 0;
        this._pitch = 0;
        this._dragging = false;

        this.setInstance();
        this.setControls();
        this.setFlyControls();
        this.setDebug();
    }

    setInstance() {
        this.instance = new THREE.PerspectiveCamera(
            58,
            this.sizes.width / this.sizes.height,
            CONFIG.CAMERA_NEAR,
            CONFIG.CAMERA_FAR,
        );
        this.instance.position.set(8, 10, 12);
        this.scene.add(this.instance);
    }

    setControls() {
        this.controls = new OrbitControls(this.instance, this.canvas);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.1;
        this.controls.minDistance = 4;
        this.controls.maxDistance = 96;
        this.controls.maxPolarAngle = Math.PI * 0.47;
        this.controls.minPolarAngle = 0.15;
        this.controls.enablePan = false;
        this.controls.rotateSpeed = device.isTouchDevice ? 0.55 : 0.75;
        this.controls.zoomSpeed = 0.8;
        this.controls.target.set(0, 0.5, 0);
        this.controls.update();
    }

    setFlyControls() {
        if (!this.orc.debug.active) return;

        this.onPointerDown = (event) => {
            if (!this.flying || event.button !== 0) return;
            this._dragging = true;
            this._lastX = event.clientX;
            this._lastY = event.clientY;
            this.captureLook();
            this.canvas.style.cursor = "grabbing";
            this.canvas.setPointerCapture(event.pointerId);
        };

        this.onPointerMove = (event) => {
            if (!this._dragging) return;
            const dx = event.clientX - this._lastX;
            const dy = event.clientY - this._lastY;
            this._lastX = event.clientX;
            this._lastY = event.clientY;
            this._yaw -= dx * LOOK_SENSITIVITY;
            this._pitch = THREE.MathUtils.clamp(
                this._pitch - dy * LOOK_SENSITIVITY,
                -PITCH_LIMIT,
                PITCH_LIMIT,
            );
            this.applyLook();
        };

        this.onPointerUp = (event) => {
            if (!this._dragging) return;
            this._dragging = false;
            this.canvas.style.cursor = this.flying ? "grab" : "";
            if (this.canvas.hasPointerCapture?.(event.pointerId)) {
                this.canvas.releasePointerCapture(event.pointerId);
            }
        };

        this.canvas.addEventListener("pointerdown", this.onPointerDown);
        window.addEventListener("pointermove", this.onPointerMove);
        window.addEventListener("pointerup", this.onPointerUp);
        window.addEventListener("pointercancel", this.onPointerUp);
    }

    setDebug() {
        if (!this.orc.debug.active) return;

        const folder = this.orc.debug.ui.addFolder("Camera");
        folder
            .add(this, "flying")
            .name("Fly")
            .onChange((enabled) => this.setFlying(enabled));
        folder.add(this, "flySpeed", 10, 400, 1).name("Fly speed");
        folder.open();
    }

    captureLook() {
        this._euler.setFromQuaternion(this.instance.quaternion, "YXZ");
        this._yaw = this._euler.y;
        this._pitch = THREE.MathUtils.clamp(
            this._euler.x,
            -PITCH_LIMIT,
            PITCH_LIMIT,
        );
    }

    applyLook() {
        this._euler.set(this._pitch, this._yaw, 0);
        this.instance.quaternion.setFromEuler(this._euler);
    }

    setFlying(enabled) {
        this.controls.enabled = !enabled;
        if (enabled) {
            this.captureLook();
            this.canvas.style.cursor = "grab";
            return;
        }

        this._dragging = false;
        this.canvas.style.cursor = "";
        this.instance.getWorldDirection(this._flyDir);
        const distance = THREE.MathUtils.clamp(
            this.instance.position.distanceTo(this.controls.target),
            this.controls.minDistance,
            this.controls.maxDistance,
        );
        this.controls.target
            .copy(this.instance.position)
            .addScaledVector(this._flyDir, distance);
        this.controls.update();
    }

    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }

    update(delta = 0) {
        if (this.flying) {
            this.updateFly(delta);
            return;
        }

        this.controls.update();
    }

    updateFly(delta) {
        if (!this.flyForwardHeld()) return;
        const dt = Math.min(delta, 0.05);
        this.instance.getWorldDirection(this._flyDir);
        this.instance.position.addScaledVector(this._flyDir, this.flySpeed * dt);
    }

    flyForwardHeld() {
        const active = document.activeElement;
        const type = active?.type;
        const typing =
            active?.tagName === "TEXTAREA" ||
            type === "text" ||
            type === "number" ||
            type === "search";
        if (typing) return false;
        return !!this.orc.stage?.input?.keys?.KeyW;
    }

    dispose() {
        if (this.onPointerDown) {
            this.canvas.removeEventListener("pointerdown", this.onPointerDown);
            window.removeEventListener("pointermove", this.onPointerMove);
            window.removeEventListener("pointerup", this.onPointerUp);
            window.removeEventListener("pointercancel", this.onPointerUp);
        }
        this.controls.dispose();
    }
}
